// Web-exclusive Profile screen for Doctors (isDoctor === true)
// Matches design from ref/doctor-pages.html Account page

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  MapPin,
  Stethoscope,
  BadgeCheck,
  FileText,
  GraduationCap,
  Lock,
  Shield,
  HelpCircle,
  Pencil,
  Building2,
  Globe,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Mail,
  MessageSquare,
  ChevronDown,
  Check,
  Search,
  X,
} from 'lucide-react';
import * as WebBrowser from 'expo-web-browser';
import * as DocumentPicker from 'expo-document-picker';
import WebLayout from '../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import {
  getProfile,
  updateProfile,
  deleteUser,
  getOverview,
  getEducation,
  uploadResume,
} from '../../services/profile';
import { getApplications } from '../../services/job';
import { submitChangePassword, submitFeedback, getProfessionsByType } from '../../services/shared';
import { logout } from '../../services/auth';
import { setLoggedOut } from '../../reducers/auth/user';
import { resetFields } from '../../reducers/profile/profile';
import { getAdministrativeDivisionLabel, getCountryData } from '../../constants/countries';
import { isAssistant, isNurses } from '../../constants/lov/professions';
import { extractProfessionTitleAndSubtitle, getLocalizedProfessionName } from '../../utils/professionUtils';
import { useProfileImageUpload } from '../../hooks/useProfileImageUpload';
import { PLACEHOLDER_PROFILE_URL } from '../../constants/placeholderImage';
import { useIsMobile } from '../../hooks/useIsMobile';

const ProfileDoctorsScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const scrollViewRef = useRef(null);
  const isMobile = useIsMobile();

  // Redux state
  const { slug, country, isDoctor } = useSelector((state) => state.user);

  // Local state
  const [activeSection, setActiveSection] = useState('personal-info');
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showFAQ, setShowFAQ] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  // Incomplete profile popup state
  const [showIncompletePopup, setShowIncompletePopup] = useState(false);

  // Profession modal state
  const [showProfessionModal, setShowProfessionModal] = useState(false);
  const [selectedProfessionIds, setSelectedProfessionIds] = useState([]);
  const [professionSearchQuery, setProfessionSearchQuery] = useState('');

  // Data fetching
  const { data: profileQuery, isLoading: profileLoading, refetch: profileRefetch } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
  });
  const profileData = profileQuery?.data?.data;

  const { data: educationQuery, isLoading: educationLoading } = useQuery({
    queryKey: ['education'],
    queryFn: getEducation,
  });
  const education = educationQuery?.data || [];

  const { data: overviewQuery, isLoading: overviewLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const overviewInfo = overviewQuery?.data?.data;

  const { data: jobsQuery } = useQuery({
    queryKey: ['applications'],
    queryFn: getApplications,
  });

  // Profession type code for filtering available professions
  const professionTypeCode = profileData?.profession?.profession_type_code;

  // Fetch available professions when modal is open
  const { data: availableProfessionsQuery, isLoading: professionsLoading } = useQuery({
    queryKey: ['availableProfessions', country, professionTypeCode],
    queryFn: () => getProfessionsByType(country || 'MY', professionTypeCode),
    enabled: showProfessionModal && !!professionTypeCode,
    staleTime: 5 * 60 * 1000,
  });
  const availableProfessions = availableProfessionsQuery?.data?.data || [];

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries(['profile']);
      setEditingSection(null);
      setShowProfessionModal(false);
      setProfessionSearchQuery('');
      setErrors({});
      showToast('Profile updated successfully!', 'success');
    },
    onError: (err) => {
      const errorData = err?.response?.data?.errors || {};
      setErrors(errorData);
      // Only show toast if there are no field-level errors
      if (Object.keys(errorData).length === 0) {
        showToast(err?.response?.data?.message || 'Failed to update. Please try again.', 'error');
      }
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: submitChangePassword,
    onSuccess: () => {
      setPasswordData({ current: '', new: '', confirm: '' });
      showToast('Password changed successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to change password', 'error');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      navigation.replace('AuthScreen');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to delete account', 'error');
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      setFeedbackText('');
      setShowFeedback(false);
      showToast('Thank you for your feedback!', 'success');
    },
    onError: () => {
      showToast('Failed to submit feedback. Please try again.', 'error');
    },
  });

  const uploadResumeMutation = useMutation({
    mutationFn: uploadResume,
    onSuccess: () => {
      queryClient.invalidateQueries(['profile']);
      showToast('Resume uploaded successfully!', 'success');
    },
    onError: () => {
      showToast('Failed to upload resume. Please try again.', 'error');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      dispatch(resetFields());
      dispatch(setLoggedOut());
      navigation.replace('AuthScreen');
    },
    onError: () => {
      dispatch(resetFields());
      dispatch(setLoggedOut());
      navigation.replace('AuthScreen');
    },
  });

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Profile image upload
  const {
    isUploading: isUploadingImage,
    triggerImagePicker,
  } = useProfileImageUpload({
    fieldName: 'profile_image',
    scope: 'personal',
    maxSizeMB: 5,
    onSuccess: () => {
      showToast('Profile picture updated successfully!', 'success');
    },
    onError: (error) => {
      showToast(error, 'error');
    },
  });

  // Country-specific labels
  const getIdDocumentLabel = (countryCode) => {
    const labels = {
      'MY': 'IC Number',
      'SG': 'NRIC Number',
      'PH': 'Valid ID',
      'ID': 'KTP Number'
    };
    return labels[countryCode] || 'National ID Number';
  };

  const getMedicalLicenseName = (countryCode) => {
    const names = {
      'MY': 'MMC',
      'SG': 'SMC',
      'PH': 'PRC',
      'ID': 'STR'
    };
    return names[countryCode] || 'Medical Registration';
  };

  const getPracticeLicenseName = (countryCode) => {
    const names = {
      'MY': 'APC',
      'SG': 'PC',
      'PH': 'PRC-ID',
      'ID': 'SIP'
    };
    return names[countryCode] || 'Practice License';
  };

  const divisionLabel = getAdministrativeDivisionLabel(country);

  // Initialize form data when profile loads
  useEffect(() => {
    if (profileData) {
      setFormData({
        // Personal info
        first_name: profileData.first_name || '',
        about: profileData.about || '',
        phone_number: profileData.phone_number || '',
        email: profileData.email || '',
        nric_passport_number: profileData.nric_passport_number || '',
        emergency_name: profileData.emergency_name || '',
        emergency_number: profileData.emergency_number || '',
        // Address
        address: profileData.address || '',
        city: profileData.city || '',
        postcode: profileData.postcode || '',
        state: profileData.state || '',
        // Medical license
        apc: profileData.apc || '',
        mmc: profileData.mmc || '',
        nsr: profileData.nsr || '',
      });
    }
  }, [profileData]);

  // Show incomplete profile popup if required fields are missing
  useEffect(() => {
    if (profileData) {
      const missingRequired =
        !profileData.first_name ||
        !profileData.phone_number ||
        !profileData.nric_passport_number ||
        (!profileData.professions?.length && !profileData.profession);

      if (missingRequired) {
        setShowIncompletePopup(true);
      }
    }
  }, [profileData]);

  // Initialize profession selection when modal opens
  useEffect(() => {
    if (showProfessionModal) {
      if (profileData?.professions && Array.isArray(profileData.professions)) {
        setSelectedProfessionIds(profileData.professions.map(p => p.id));
      } else {
        setSelectedProfessionIds([]);
      }
    }
  }, [showProfessionModal]);

  // Calculate profile completion
  const profileCompletion = useMemo(() => {
    if (!profileData) return 0;

    let completed = 0;
    const total = 10;

    if (profileData.about) completed++;
    if (profileData.phone_number) completed++;
    if (profileData.nric_passport_number) completed++;
    if (profileData.emergency_name) completed++;
    if (profileData.emergency_number) completed++;
    if (profileData.address) completed++;
    if (profileData.professions?.length > 0 || profileData.profession) completed++;
    if (profileData.apc) completed++;
    if (profileData.resume) completed++;
    if (profileData.education?.length > 0) completed++;

    return Math.round((completed / total) * 100);
  }, [profileData]);

  // Section refs for scrolling
  const sectionRefs = useRef({});

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    if (Platform.OS === 'web' && sectionRefs.current[sectionId]) {
      sectionRefs.current[sectionId].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper to get error message for a field (handles array of errors)
  const getFieldError = (fieldName) => {
    const fieldErrors = errors[fieldName];
    if (!fieldErrors) return null;
    if (Array.isArray(fieldErrors)) {
      return fieldErrors.join('\n');
    }
    return fieldErrors;
  };

  // Handle form changes
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Save handlers
  const handleSavePersonalInfo = () => {
    updateProfileMutation.mutate({
      scope: 'personal',
      about: formData.about,
      first_name: formData.first_name,
      phone_number: formData.phone_number,
      nric_passport_number: formData.nric_passport_number,
      emergency_name: formData.emergency_name,
      emergency_number: formData.emergency_number,
    });
  };

  const handleSaveAddress = () => {
    updateProfileMutation.mutate({
      scope: 'address',
      address: formData.address,
      city: formData.city,
      postcode: formData.postcode,
      state: formData.state,
    });
  };

  const handleSaveMedicalLicense = () => {
    updateProfileMutation.mutate({
      scope: 'license',
      apc: formData.apc,
      mmc: formData.mmc,
      nsr: formData.nsr,
    });
  };

  const handleSaveProfessions = () => {
    if (selectedProfessionIds.length === 0) {
      showToast('Please select at least one profession', 'error');
      return;
    }

    if (!availableProfessions || availableProfessions.length === 0) {
      showToast('Unable to save: profession data not loaded', 'error');
      return;
    }

    const selectedProfs = availableProfessions.filter(p =>
      selectedProfessionIds.includes(p.id)
    );

    if (selectedProfs.length === 0) {
      showToast('Error: Selected professions could not be found', 'error');
      return;
    }

    updateProfileMutation.mutate({
      scope: 'professions',
      professions: selectedProfs,
      profession_ids: selectedProfessionIds,
      profession_id: selectedProfessionIds[0],
      expertise: selectedProfessionIds[0],
    });
  };

  const handleCloseProfessionModal = () => {
    setShowProfessionModal(false);
    setProfessionSearchQuery('');
  };

  const handleChangePassword = () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      showToast('Please fill in all password fields', 'error');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (passwordData.new.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }

    updatePasswordMutation.mutate({
      current_password: passwordData.current,
      password: passwordData.new,
      password_confirmation: passwordData.confirm,
    });
  };

  const handleUploadResume = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        let webFile = asset.file;
        if (!webFile && asset.uri) {
          const blob = await fetch(asset.uri).then((r) => r.blob());
          webFile = new File([blob], asset.name || 'resume.pdf', {
            type: asset.mimeType || blob.type || 'application/pdf',
          });
        }
        if (!webFile) {
          showToast('Failed to read selected file', 'error');
          return;
        }
        uploadResumeMutation.mutate(webFile);
      }
    } catch (error) {
      showToast('Failed to select file', 'error');
    }
  };

  const checkPendingJobs = () => {
    let noPendingJobs = true;
    if (jobsQuery?.data?.data) {
      jobsQuery.data.data.forEach((job) => {
        if ([0, 1, 2, 3].includes(job.job_application_status_code)) {
          noPendingJobs = false;
        }
      });
    }
    return noPendingJobs;
  };

  const handleDeleteAccount = () => {
    const isDeletable = checkPendingJobs();

    if (!isDeletable) {
      if (Platform.OS === 'web') {
        window.alert('You have pending jobs. Please complete or cancel them before deleting your account.');
      } else {
        Alert.alert('Cannot Delete', 'You have pending jobs. Please complete or cancel them before deleting your account.');
      }
      return;
    }

    if (Platform.OS === 'web') {
      if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
        deleteUserMutation.mutate(slug);
      }
    } else {
      Alert.alert(
        'Delete Account',
        'Are you sure you want to delete your account? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: () => deleteUserMutation.mutate(slug) },
        ]
      );
    }
  };

  const handleSubmitFeedback = () => {
    if (!feedbackText.trim()) {
      showToast('Please enter your feedback', 'error');
      return;
    }
    submitFeedbackMutation.mutate({
      type: 'feedback',
      message: feedbackText,
    });
  };

  const handleLogout = () => {
    if (Platform.OS === 'web') {
      if (window.confirm('Are you sure you want to logout?')) {
        logoutMutation.mutate();
      }
    } else {
      Alert.alert(
        'Logout',
        'Are you sure you want to logout?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Logout', onPress: () => logoutMutation.mutate() },
        ]
      );
    }
  };

  const isLoading = profileLoading;

  if (isLoading) {
    return (
      <WebLayout>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={webColors.primary} />
          <Text style={styles.loadingText}>Loading profile...</Text>
        </View>
      </WebLayout>
    );
  }

  // Navigation items - conditionally include Medical License for non-assistants
  const showMedicalLicense = profileData && !isAssistant(profileData.expertise);
  const navItems = [
    { id: 'personal-info', Icon: User, label: 'Personal Info' },
    { id: 'address', Icon: MapPin, label: 'Address' },
    { id: 'profession', Icon: Stethoscope, label: 'Profession' },
    ...(showMedicalLicense ? [{ id: 'medical-license', Icon: BadgeCheck, label: 'Medical License' }] : []),
    { id: 'resume', Icon: FileText, label: 'Resume' },
    { id: 'education', Icon: GraduationCap, label: 'Education' },
    { id: 'change-password', Icon: Lock, label: 'Change Password' },
    { id: 'privacy-policy', Icon: Shield, label: 'Privacy Policy' },
    { id: 'help-support', Icon: HelpCircle, label: 'Help & Support' },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How do I create an account on SuperMedic?',
      answer: 'Download the SuperMedic app from the App Store or Google Play. Click "Sign Up" and follow the registration process. You\'ll need to verify your email and provide your professional credentials.',
    },
    {
      question: 'How do I apply for a job?',
      answer: 'Browse available jobs from the Jobs tab. Click on a job to view details, then click "Apply" to submit your application. Make sure your profile is complete to increase your chances.',
    },
    {
      question: 'What documents do I need to upload?',
      answer: 'You need to upload your medical license (APC/MMC), valid ID (IC/NRIC), and optionally your resume and certifications. These help verify your credentials and make your profile more attractive to employers.',
    },
    {
      question: 'How do I track my job applications?',
      answer: 'Go to the Activity tab to see all your job applications. You can filter by status (Pending, Approved, Completed) to track your progress.',
    },
    {
      question: 'What is a Locum position?',
      answer: 'A Locum position is a temporary or short-term healthcare role. It offers flexibility to work on your schedule while gaining experience at different facilities.',
    },
    {
      question: 'How do I contact an employer?',
      answer: 'Once your application is approved, you can view the employer\'s contact details in the job listing. Communication should be professional and job-related.',
    },
  ];

  // Get profession display
  const getProfessionDisplay = () => {
    if (profileData?.professions && profileData.professions.length > 0) {
      const getCountrySpecificProfession = (professionData) => {
        const professionType = professionData.profession_type;
        switch (country) {
          case 'ID':
            return {
              profession: professionData.indonesia_name,
              type: professionType?.malay_name,
              countryLabel: 'Indonesia',
              original: professionData
            };
          case 'SG':
            return {
              profession: professionData.singapore_name,
              type: professionType?.english_name,
              countryLabel: 'Singapore',
              original: professionData
            };
          case 'PH':
            return {
              profession: professionData.philippines_name,
              type: professionType?.english_name,
              countryLabel: 'Philippines',
              original: professionData
            };
          case 'IN':
          case 'VN':
          case 'KH':
          case 'TH':
          case 'LA':
            return {
              profession: professionData.international_name,
              type: professionType?.english_name,
              countryLabel: getCountryData(country).name,
              original: professionData
            };
          case 'MY':
          default:
            return {
              profession: professionData.malaysia_name,
              type: professionType?.english_name,
              countryLabel: 'Malaysia',
              original: professionData
            };
        }
      };

      return profileData.professions.map(prof => getCountrySpecificProfession(prof));
    }
    return [];
  };

  const professionsList = getProfessionDisplay();

  return (
    <WebLayout>
      {/* Toast Notification */}
      {toast && (
        <View style={[
          styles.toast,
          toast.type === 'success' && styles.toastSuccess,
          toast.type === 'error' && styles.toastError,
        ]}>
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      {/* Incomplete Profile Popup */}
      {showIncompletePopup && (
        <View style={styles.incompleteOverlay}>
          <View style={styles.incompletePopup}>
            <View style={styles.incompletePopupIconRow}>
              <View style={styles.incompletePopupIconCircle}>
                <Shield size={28} color={webColors.warning || '#f59e0b'} />
              </View>
            </View>
            <Text style={styles.incompletePopupTitle}>
              Action Required: Complete Required Information
            </Text>
            <Text style={styles.incompletePopupCaption}>
              Fields marked with * must be filled in before you can apply for jobs.
            </Text>
            <TouchableOpacity
              style={styles.incompletePopupBtn}
              onPress={() => {
                setShowIncompletePopup(false);
                scrollToSection('personal-info');
              }}
            >
              <Text style={styles.incompletePopupBtnText}>Complete Now</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.incompletePopupDismiss}
              onPress={() => setShowIncompletePopup(false)}
            >
              <Text style={styles.incompletePopupDismissText}>Dismiss</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Profession Edit Modal */}
      {showProfessionModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Professions</Text>
              <TouchableOpacity
                onPress={handleCloseProfessionModal}
                style={styles.modalCloseBtn}
              >
                <X size={20} color={webColors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Profession Type Display */}
            {professionsList[0]?.type && (
              <View style={styles.modalProfessionType}>
                <Building2 size={14} color={webColors.textSecondary} />
                <Text style={styles.modalProfessionTypeText}>
                  {professionsList[0].type}
                </Text>
                <Text style={styles.modalProfessionTypeHint}>
                  (Select specializations within this type)
                </Text>
              </View>
            )}

            {/* Search Input */}
            <View style={styles.modalSearchWrapper}>
              <Search size={16} color={webColors.textMuted} style={styles.modalSearchIcon} />
              <TextInput
                style={styles.modalSearchInput}
                placeholder="Search professions..."
                placeholderTextColor={webColors.textMuted}
                value={professionSearchQuery}
                onChangeText={setProfessionSearchQuery}
              />
            </View>

            {/* Professions List */}
            <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
              {professionsLoading ? (
                <View style={styles.modalLoadingContainer}>
                  <ActivityIndicator size="small" color={webColors.primary} />
                  <Text style={styles.modalLoadingText}>Loading professions...</Text>
                </View>
              ) : (
                availableProfessions
                  .filter(prof => {
                    const name = getLocalizedProfessionName(prof, country) || '';
                    return name.toLowerCase().includes(professionSearchQuery.toLowerCase());
                  })
                  .map((profession) => {
                    const isSelected = selectedProfessionIds.includes(profession.id);
                    const profName = getLocalizedProfessionName(profession, country);
                    const { title, subtitle } = extractProfessionTitleAndSubtitle(profName);

                    if (!title) return null;

                    return (
                      <TouchableOpacity
                        key={profession.id}
                        style={[
                          styles.modalProfessionItem,
                          isSelected && styles.modalProfessionItemSelected,
                        ]}
                        onPress={() => {
                          setSelectedProfessionIds(prev =>
                            prev.includes(profession.id)
                              ? prev.filter(id => id !== profession.id)
                              : [...prev, profession.id]
                          );
                        }}
                      >
                        <View style={[
                          styles.modalCheckbox,
                          isSelected && styles.modalCheckboxSelected,
                        ]}>
                          {isSelected && <Check size={14} color="#ffffff" />}
                        </View>
                        <View style={styles.modalProfessionInfo}>
                          <Text style={[
                            styles.modalProfessionTitle,
                            isSelected && styles.modalProfessionTitleSelected,
                          ]}>
                            {title}
                          </Text>
                          {subtitle && (
                            <Text style={styles.modalProfessionSubtitle}>{subtitle}</Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })
              )}
              {!professionsLoading && availableProfessions.filter(prof => {
                const name = getLocalizedProfessionName(prof, country) || '';
                return name.toLowerCase().includes(professionSearchQuery.toLowerCase());
              }).length === 0 && (
                <View style={styles.modalEmptyState}>
                  <Text style={styles.modalEmptyStateText}>
                    {professionSearchQuery ? `No professions found matching "${professionSearchQuery}"` : 'No professions available'}
                  </Text>
                </View>
              )}
            </ScrollView>

            {/* Modal Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={handleCloseProfessionModal}
              >
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalSaveBtn,
                  (selectedProfessionIds.length === 0 || updateProfileMutation.isPending || professionsLoading) &&
                  styles.modalSaveBtnDisabled,
                ]}
                onPress={handleSaveProfessions}
                disabled={selectedProfessionIds.length === 0 || updateProfileMutation.isPending || professionsLoading}
              >
                <Text style={styles.modalSaveBtnText}>
                  {updateProfileMutation.isPending ? 'Saving...' :
                   professionsLoading ? 'Loading...' :
                   `Save (${selectedProfessionIds.length})`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Account</Text>
          <Text style={styles.pageSubtitle}>Manage your profile and settings</Text>
        </View>

        {/* Two Column Layout */}
        <View style={[styles.accountLayout, isMobile && styles.accountLayoutMobile]}>
          {/* Sidebar */}
          <View style={[styles.sidebar, isMobile && styles.sidebarMobile]}>
            {/* Profile Card */}
            <View style={styles.profileCard}>
              <TouchableOpacity
                style={styles.profilePictureWrapper}
                onPress={triggerImagePicker}
                disabled={isUploadingImage}
              >
                {isUploadingImage ? (
                  <View style={styles.userAvatar}>
                    <ActivityIndicator size="small" color="#ffffff" />
                  </View>
                ) : (
                  <Image
                    source={{ uri: profileData?.profile_image
                      ? 'data:image/jpg;base64,' + profileData.profile_image
                      : PLACEHOLDER_PROFILE_URL }}
                    style={styles.userAvatarImage}
                  />
                )}
                <View style={[styles.profilePictureOverlay, isUploadingImage && styles.profilePictureOverlayDisabled]}>
                  <Pencil size={14} color="#ffffff" />
                </View>
              </TouchableOpacity>

              <Text style={styles.userName}>
                {profileData?.first_name}
              </Text>
              <Text style={styles.userRole}>
                {professionsList[0]?.profession || 'Healthcare Professional'}
              </Text>

              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${profileCompletion}%` }]} />
              </View>
              <Text style={styles.progressText}>Profile {profileCompletion}% Complete</Text>

              {profileCompletion < 100 && (
                <TouchableOpacity
                  style={styles.completeProfileBtn}
                  onPress={() => scrollToSection('personal-info')}
                >
                  <Text style={styles.completeProfileBtnText}>Complete Profile</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Navigation Menu (desktop only) */}
            {!isMobile && (
            <View style={styles.navMenu}>
              {navItems.map((item, index) => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.navItem,
                    activeSection === item.id && styles.navItemActive,
                    index === navItems.length - 1 && styles.navItemLast,
                  ]}
                  onPress={() => scrollToSection(item.id)}
                >
                  <View style={styles.navItemIcon}>
                    <item.Icon
                      size={18}
                      color={activeSection === item.id ? webColors.primaryDark : webColors.textSecondary}
                    />
                  </View>
                  <Text style={[
                    styles.navItemLabel,
                    activeSection === item.id && styles.navItemLabelActive,
                  ]}>
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            )}
          </View>

          {/* Content Area */}
          <View style={[styles.accountContent, isMobile && styles.accountContentMobile]}>
            <View style={styles.contentSection}>
              {/* Personal Information */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['personal-info'] = el}
              >
                <Text style={styles.formSectionTitle}>Personal Information</Text>

                <View style={styles.formGrid}>
                  <FormField
                    label="Name"
                    value={formData.first_name}
                    onChange={(v) => updateField('first_name', v)}
                    editable={editingSection === 'personal-info'}
                    error={getFieldError('first_name')}
                    fullWidth
                  />
                  <FormField
                    label="About"
                    value={formData.about}
                    onChange={(v) => updateField('about', v)}
                    editable={editingSection === 'personal-info'}
                    fullWidth
                    multiline
                    placeholder="Tell employers about yourself..."
                    error={getFieldError('about')}
                  />
                  <FormField
                    label="Phone"
                    value={formData.phone_number}
                    onChange={(v) => updateField('phone_number', v)}
                    editable={editingSection === 'personal-info'}
                    keyboardType="phone-pad"
                    error={getFieldError('phone_number')}
                  />
                  <FormField
                    label="Email"
                    value={formData.email}
                    editable={false}
                    error={getFieldError('email')}
                  />
                  <FormField
                    label={getIdDocumentLabel(country)}
                    value={formData.nric_passport_number}
                    onChange={(v) => updateField('nric_passport_number', v)}
                    editable={editingSection === 'personal-info'}
                    required
                    error={getFieldError('nric_passport_number')}
                  />
                  <FormField
                    label="Emergency Contact Name"
                    value={formData.emergency_name}
                    onChange={(v) => updateField('emergency_name', v)}
                    editable={editingSection === 'personal-info'}
                    error={getFieldError('emergency_name')}
                  />
                  <FormField
                    label="Emergency Mobile No."
                    value={formData.emergency_number}
                    onChange={(v) => updateField('emergency_number', v)}
                    editable={editingSection === 'personal-info'}
                    keyboardType="phone-pad"
                    error={getFieldError('emergency_number')}
                  />
                </View>

                {editingSection !== 'personal-info' ? (
                  <TouchableOpacity
                    style={styles.btnSecondary}
                    onPress={() => setEditingSection('personal-info')}
                  >
                    <Text style={styles.btnSecondaryText}>Edit Personal Information</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.sectionEditActions}>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => {
                        setEditingSection(null);
                        setErrors({});
                      }}
                    >
                      <Text style={styles.cancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.saveBtn, updateProfileMutation.isPending && styles.saveBtnDisabled]}
                      onPress={handleSavePersonalInfo}
                      disabled={updateProfileMutation.isPending}
                    >
                      <Text style={styles.saveBtnText}>
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Personal Address */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['address'] = el}
              >
                <Text style={styles.formSectionTitle}>Personal Address</Text>

                <View style={styles.formGrid}>
                  <FormField
                    label="Address"
                    value={formData.address}
                    onChange={(v) => updateField('address', v)}
                    editable={editingSection === 'address'}
                    fullWidth
                    multiline
                    error={getFieldError('address')}
                  />
                  <FormField
                    label="City"
                    value={formData.city}
                    onChange={(v) => updateField('city', v)}
                    editable={editingSection === 'address'}
                    error={getFieldError('city')}
                  />
                  <FormField
                    label="Postcode"
                    value={formData.postcode}
                    onChange={(v) => updateField('postcode', v)}
                    editable={editingSection === 'address'}
                    error={getFieldError('postcode')}
                  />
                  <FormField
                    label={divisionLabel}
                    value={formData.state}
                    onChange={(v) => updateField('state', v)}
                    editable={editingSection === 'address'}
                    error={getFieldError('state')}
                  />
                </View>

                {editingSection !== 'address' ? (
                  <TouchableOpacity
                    style={styles.btnSecondary}
                    onPress={() => setEditingSection('address')}
                  >
                    <Text style={styles.btnSecondaryText}>Edit Address</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.sectionEditActions}>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => {
                        setEditingSection(null);
                        setErrors({});
                      }}
                    >
                      <Text style={styles.cancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.saveBtn, updateProfileMutation.isPending && styles.saveBtnDisabled]}
                      onPress={handleSaveAddress}
                      disabled={updateProfileMutation.isPending}
                    >
                      <Text style={styles.saveBtnText}>
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Profession */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['profession'] = el}
              >
                <Text style={styles.formSectionTitle}>Profession</Text>

                {professionsList.length > 0 ? (
                  <View style={styles.professionDisplay}>
                    {/* Profession Type Header */}
                    {professionsList[0]?.type && (
                      <View style={styles.professionTypeRow}>
                        <View style={styles.professionTypeIcon}>
                          <Building2 size={14} color={webColors.textSecondary} />
                        </View>
                        <Text style={styles.professionTypeText}>{professionsList[0].type}</Text>
                      </View>
                    )}

                    {/* Professions as pills */}
                    <View style={styles.professionPillsContainer}>
                      {professionsList.map((prof, index) => {
                        const { title, subtitle } = extractProfessionTitleAndSubtitle(prof.profession);
                        return (
                          <View key={index} style={styles.professionPill}>
                            <Text style={styles.professionPillTitle}>{title}</Text>
                            {subtitle && (
                              <Text style={styles.professionPillSubtitle}>{subtitle}</Text>
                            )}
                          </View>
                        );
                      })}
                    </View>

                    {/* International names if different */}
                    {professionsList.some(prof =>
                      prof.original.international_name &&
                      prof.original.international_name !== prof.profession
                    ) && (
                      <View style={styles.professionIntlRow}>
                        <View style={styles.professionIntlIcon}>
                          <Globe size={12} color={webColors.textMuted} />
                        </View>
                        <Text style={styles.professionIntlText}>
                          {professionsList
                            .filter(prof => prof.original.international_name && prof.original.international_name !== prof.profession)
                            .map(prof => prof.original.international_name)
                            .join(' • ')
                          }
                        </Text>
                      </View>
                    )}

                    {/* Country indicator */}
                    <View style={styles.professionCountryRow}>
                      <View style={styles.professionCountryIcon}>
                        <MapPin size={12} color={webColors.textMuted} />
                      </View>
                      <Text style={styles.professionCountryText}>
                        {professionsList[0]?.countryLabel} Classification
                      </Text>
                    </View>
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyStateText}>No profession selected</Text>
                  </View>
                )}

                <TouchableOpacity
                  style={styles.btnSecondary}
                  onPress={() => setShowProfessionModal(true)}
                >
                  <Text style={styles.btnSecondaryText}>Edit Profession</Text>
                </TouchableOpacity>
              </View>

              {/* Medical License - Hidden for assistants */}
              {showMedicalLicense && (
                <View
                  style={styles.formSection}
                  ref={el => sectionRefs.current['medical-license'] = el}
                >
                  <Text style={styles.formSectionTitle}>Medical License</Text>

                  <View style={styles.formGrid}>
                    <FormField
                      label={`${getPracticeLicenseName(country)} No.`}
                      value={formData.apc}
                      onChange={(v) => updateField('apc', v)}
                      editable={editingSection === 'medical-license'}
                      error={getFieldError('apc')}
                    />
                    {!isNurses(profileData?.expertise) && country && country !== 'PH' && (
                      <FormField
                        label={`${getMedicalLicenseName(country)} No.`}
                        value={formData.mmc}
                        onChange={(v) => updateField('mmc', v)}
                        editable={editingSection === 'medical-license'}
                        error={getFieldError('mmc')}
                      />
                    )}
                    {profileData?.nsr && (
                      <FormField
                        label="NSR No."
                        value={formData.nsr}
                        onChange={(v) => updateField('nsr', v)}
                        editable={editingSection === 'medical-license'}
                        error={getFieldError('nsr')}
                      />
                    )}
                  </View>

                  {editingSection !== 'medical-license' ? (
                    <TouchableOpacity
                      style={styles.btnSecondary}
                      onPress={() => setEditingSection('medical-license')}
                    >
                      <Text style={styles.btnSecondaryText}>Edit Medical License</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.sectionEditActions}>
                      <TouchableOpacity
                        style={styles.cancelBtn}
                        onPress={() => {
                          setEditingSection(null);
                          setErrors({});
                        }}
                      >
                        <Text style={styles.cancelBtnText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.saveBtn, updateProfileMutation.isPending && styles.saveBtnDisabled]}
                        onPress={handleSaveMedicalLicense}
                        disabled={updateProfileMutation.isPending}
                      >
                        <Text style={styles.saveBtnText}>
                          {updateProfileMutation.isPending ? 'Saving...' : 'Save'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              )}

              {/* Resume */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['resume'] = el}
              >
                <Text style={styles.formSectionTitle}>Resume</Text>

                {profileData?.resume ? (
                  <TouchableOpacity
                    style={styles.resumeDisplay}
                    onPress={() => WebBrowser.openBrowserAsync(profileData.resume)}
                  >
                    <View style={styles.resumeIcon}>
                      <FileText size={24} color="#22c55e" />
                    </View>
                    <View style={styles.resumeInfo}>
                      <Text style={styles.resumeTitle}>View Uploaded Resume</Text>
                      <Text style={styles.resumeStatus}>Click to open in browser</Text>
                    </View>
                    <ArrowRight size={16} color={webColors.textMuted} />
                  </TouchableOpacity>
                ) : (
                  <View style={styles.emptyState}>
                    <View style={styles.emptyStateIcon}>
                      <FileText size={40} color={webColors.textMuted} />
                    </View>
                    <Text style={styles.emptyStateText}>No resume uploaded</Text>
                    <Text style={styles.emptyStateHint}>Upload your resume to strengthen your profile</Text>
                  </View>
                )}

                <TouchableOpacity
                  style={[styles.btnSecondary, uploadResumeMutation.isPending && styles.btnDisabled]}
                  onPress={handleUploadResume}
                  disabled={uploadResumeMutation.isPending}
                >
                  <Text style={styles.btnSecondaryText}>
                    {uploadResumeMutation.isPending
                      ? 'Uploading...'
                      : profileData?.resume
                        ? 'Update Resume'
                        : 'Upload Resume'}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Education */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['education'] = el}
              >
                <Text style={styles.formSectionTitle}>Education</Text>

                {profileData?.education?.length > 0 ? (
                  <View style={styles.educationList}>
                    {profileData.education.map((edu, index) => (
                      <View key={index} style={styles.educationItem}>
                        <View style={styles.educationIcon}>
                          <GraduationCap size={24} color="#f59e0b" />
                        </View>
                        <View style={styles.educationInfo}>
                          <Text style={styles.educationDegree}>{edu.name}</Text>
                          <Text style={styles.educationSchool}>{edu.school}</Text>
                          <Text style={styles.educationYear}>{edu.year}</Text>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <View style={styles.emptyStateIcon}>
                      <GraduationCap size={40} color={webColors.textMuted} />
                    </View>
                    <Text style={styles.emptyStateText}>No education added</Text>
                    <Text style={styles.emptyStateHint}>Add your educational background to strengthen your profile</Text>
                  </View>
                )}

                <TouchableOpacity
                  style={styles.btnSecondary}
                  onPress={() => navigation.navigate('EditEducationScreen')}
                >
                  <Text style={styles.btnSecondaryText}>Edit Education</Text>
                </TouchableOpacity>
              </View>

              {/* Change Password */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['change-password'] = el}
              >
                <Text style={styles.formSectionTitle}>Change Password</Text>

                <View style={styles.formGrid}>
                  <View style={styles.formGroupFullWidth}>
                    <Text style={styles.formLabel}>Current Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.current}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, current: v }))}
                      secureTextEntry
                      placeholder="Enter current password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                  <View style={[styles.formGroup, isMobile && styles.formGroupMobile]}>
                    <Text style={styles.formLabel}>New Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.new}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, new: v }))}
                      secureTextEntry
                      placeholder="Enter new password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                  <View style={[styles.formGroup, isMobile && styles.formGroupMobile]}>
                    <Text style={styles.formLabel}>Confirm New Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.confirm}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, confirm: v }))}
                      secureTextEntry
                      placeholder="Confirm new password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                </View>

                <TouchableOpacity
                  style={[styles.btnSecondary, updatePasswordMutation.isPending && styles.btnDisabled]}
                  onPress={handleChangePassword}
                  disabled={updatePasswordMutation.isPending}
                >
                  <Text style={styles.btnSecondaryText}>
                    {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Privacy Policy */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['privacy-policy'] = el}
              >
                <Text style={styles.formSectionTitle}>Privacy Policy</Text>

                <View style={styles.privacyBox}>
                  <Text style={styles.privacyText}>
                    SuperMedic is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information.
                  </Text>
                  <Text style={styles.privacyText}>
                    <Text style={styles.privacyBold}>Information We Collect:</Text> We collect information you provide when creating an account, applying for jobs, and using our services.
                  </Text>
                  <Text style={styles.privacyText}>
                    <Text style={styles.privacyBold}>How We Use Your Information:</Text> Your data is used to connect you with healthcare employers, improve our services, and communicate important updates.
                  </Text>
                </View>

                <TouchableOpacity onPress={() => {}} style={styles.linkRow}>
                  <Text style={styles.linkText}>Read Full Privacy Policy</Text>
                  <ArrowRight size={14} color={webColors.primary} />
                </TouchableOpacity>
              </View>

              {/* Help & Support */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['help-support'] = el}
              >
                <Text style={styles.formSectionTitle}>Help & Support</Text>

                {!showFAQ && !showFeedback ? (
                  <View style={styles.helpOptions}>
                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => setShowFAQ(true)}
                    >
                      <View style={styles.helpOptionIcon}>
                        <BookOpen size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>FAQs</Text>
                        <Text style={styles.helpOptionDesc}>Find answers to common questions</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => Linking.openURL('mailto:support@supermedic.health')}
                    >
                      <View style={styles.helpOptionIcon}>
                        <Mail size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Email Support</Text>
                        <Text style={styles.helpOptionDesc}>support@supermedic.health</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => console.log('https://www.youtube.com/@supermedic')}
                    >
                      <View style={styles.helpOptionIcon}>
                        <GraduationCap size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Learn How to Use SuperMedic</Text>
                        <Text style={styles.helpOptionDesc}>Watch tutorials on YouTube</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => setShowFeedback(true)}
                    >
                      <View style={styles.helpOptionIcon}>
                        <MessageSquare size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Give Us Feedback</Text>
                        <Text style={styles.helpOptionDesc}>Share your thoughts and suggestions</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>
                  </View>
                ) : showFAQ ? (
                  <View>
                    <TouchableOpacity
                      style={styles.backButton}
                      onPress={() => setShowFAQ(false)}
                    >
                      <View style={styles.backButtonContent}>
                        <ArrowLeft size={14} color={webColors.textSecondary} />
                        <Text style={styles.backButtonText}>Back to Help & Support</Text>
                      </View>
                    </TouchableOpacity>

                    <View style={styles.faqList}>
                      {faqItems.map((item, index) => (
                        <TouchableOpacity
                          key={index}
                          style={[
                            styles.faqItem,
                            expandedFAQ === index && styles.faqItemOpen,
                          ]}
                          onPress={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                        >
                          <View style={styles.faqQuestion}>
                            <Text style={[
                              styles.faqQuestionText,
                              expandedFAQ === index && styles.faqQuestionTextOpen,
                            ]}>
                              {item.question}
                            </Text>
                            <View style={[
                              styles.faqArrow,
                              expandedFAQ === index && styles.faqArrowOpen,
                            ]}>
                              <ChevronDown
                                size={16}
                                color={expandedFAQ === index ? webColors.primary : webColors.textMuted}
                              />
                            </View>
                          </View>
                          {expandedFAQ === index && (
                            <Text style={styles.faqAnswer}>{item.answer}</Text>
                          )}
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                ) : (
                  <View>
                    <TouchableOpacity
                      style={styles.backButton}
                      onPress={() => setShowFeedback(false)}
                    >
                      <View style={styles.backButtonContent}>
                        <ArrowLeft size={14} color={webColors.textSecondary} />
                        <Text style={styles.backButtonText}>Back to Help & Support</Text>
                      </View>
                    </TouchableOpacity>

                    <View style={styles.feedbackForm}>
                      <Text style={styles.formLabel}>Your Feedback</Text>
                      <TextInput
                        style={styles.feedbackTextarea}
                        value={feedbackText}
                        onChangeText={setFeedbackText}
                        placeholder="Tell us what you think about SuperMedic. Your feedback helps us improve!"
                        placeholderTextColor={webColors.textMuted}
                        multiline
                        numberOfLines={6}
                      />
                      <TouchableOpacity
                        style={[styles.btnPrimary, submitFeedbackMutation.isPending && styles.btnDisabled]}
                        onPress={handleSubmitFeedback}
                        disabled={submitFeedbackMutation.isPending}
                      >
                        <Text style={styles.btnPrimaryText}>
                          {submitFeedbackMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>

              {/* Delete Account */}
              <View style={styles.deleteSection}>
                <TouchableOpacity
                  style={styles.btnDelete}
                  onPress={handleDeleteAccount}
                >
                  <Text style={styles.btnDeleteText}>Delete Account</Text>
                </TouchableOpacity>
              </View>

              {/* Logout */}
              <View style={styles.logoutSection}>
                <TouchableOpacity
                  style={[styles.btnLogout, logoutMutation.isPending && styles.btnDisabled]}
                  onPress={handleLogout}
                  disabled={logoutMutation.isPending}
                >
                  <Text style={styles.btnLogoutText}>
                    {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>
    </WebLayout>
  );
};

// Form Field Component
const FormField = ({
  label,
  value,
  onChange,
  editable = true,
  multiline = false,
  fullWidth = false,
  placeholder = '',
  keyboardType = 'default',
  required = false,
  hint = '',
  error = null,
}) => {
  const isMobile = useIsMobile();
  return (
  <View style={[styles.formGroup, fullWidth && styles.formGroupFullWidth, isMobile && styles.formGroupMobile]}>
    <Text style={styles.formLabel}>
      {label}
      {required && <Text style={styles.requiredStar}> *</Text>}
    </Text>
    {editable ? (
      <TextInput
        style={[
          styles.formInput,
          multiline && styles.formInputMultiline,
          !editable && styles.formInputReadonly,
          error && styles.formInputError,
        ]}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        placeholderTextColor={webColors.textMuted}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
        keyboardType={keyboardType}
        editable={editable}
      />
    ) : (
      <View style={styles.formValueContainer}>
        <Text style={styles.formValue}>{value || '-'}</Text>
      </View>
    )}
    {hint && <Text style={styles.formHint}>{hint}</Text>}
    {error && <Text style={styles.fieldError}>{error}</Text>}
  </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    fontSize: 16,
    color: webColors.textMuted,
    marginTop: 12,
  },

  // Toast
  toast: {
    position: 'absolute',
    top: 80,
    right: 20,
    backgroundColor: webColors.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    zIndex: 1000,
    minWidth: 300,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },

  // Page Header
  pageHeader: {
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Layout
  accountLayout: {
    flexDirection: 'row',
    gap: 24,
  },

  // Sidebar
  sidebar: {
    width: 280,
    position: Platform.OS === 'web' ? 'sticky' : 'relative',
    top: 100,
    alignSelf: 'flex-start',
  },
  profileCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    marginBottom: 16,
  },
  profilePictureWrapper: {
    position: 'relative',
    width: 80,
    height: 80,
    marginBottom: 12,
  },
  userAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userAvatarImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  userAvatarText: {
    fontSize: 28,
    fontWeight: '700',
    color: '#ffffff',
  },
  profilePictureOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    backgroundColor: webColors.primary,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  profilePictureOverlayDisabled: {
    opacity: 0.5,
  },
  editIcon: {
    fontSize: 14,
    color: '#ffffff',
  },
  userName: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
    textAlign: 'center',
  },
  userRole: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 12,
    textAlign: 'center',
  },
  progressBar: {
    width: '100%',
    height: 8,
    backgroundColor: webColors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: webColors.primary,
    borderRadius: 4,
  },
  progressText: {
    fontSize: 13,
    color: webColors.textMuted,
    marginBottom: 12,
  },
  completeProfileBtn: {
    width: '100%',
    backgroundColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    ...webShadows.button,
  },
  completeProfileBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },

  // Navigation Menu
  navMenu: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    overflow: 'hidden',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
  },
  navItemLast: {
    borderBottomWidth: 0,
  },
  navItemActive: {
    backgroundColor: webColors.primaryLight,
    borderLeftColor: webColors.primary,
  },
  navItemIcon: {
    fontSize: 18,
  },
  navItemLabel: {
    fontSize: 15,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  navItemLabelActive: {
    color: webColors.primaryDark,
    fontWeight: '600',
  },

  // Content Area
  accountContent: {
    flex: 1,
  },
  contentSection: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    padding: 24,
  },

  // Form Sections
  formSection: {
    marginBottom: 32,
  },
  formSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  editSectionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 6,
  },
  editSectionBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  editActions: {
    flexDirection: 'row',
    gap: 8,
  },
  cancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  saveBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: webColors.success,
    borderRadius: 8,
  },
  saveBtnDisabled: {
    opacity: 0.6,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  sectionEditActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },

  // Form Grid
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  formGroup: {
    width: Platform.OS === 'web' ? 'calc(50% - 10px)' : '48%',
  },
  formGroupFullWidth: {
    width: '100%',
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  requiredStar: {
    color: webColors.error,
  },
  formInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
  },
  formInputMultiline: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  formInputReadonly: {
    backgroundColor: webColors.backgroundAlt,
  },
  formValueContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  formValue: {
    fontSize: 15,
    color: webColors.textPrimary,
  },
  formHint: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
  },
  formInputError: {
    borderColor: webColors.error,
  },
  fieldError: {
    color: webColors.error,
    fontSize: 12,
    marginTop: 4,
  },

  // Empty State
  emptyState: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
  emptyStateIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyStateText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  emptyStateHint: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
    textAlign: 'center',
  },

  // Profession Display
  professionDisplay: {
    marginBottom: 16,
  },
  professionTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  professionTypeIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  professionTypeText: {
    fontSize: 14,
    color: webColors.textSecondary,
    fontWeight: '500',
  },
  professionPillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  professionPill: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  professionPillTitle: {
    fontSize: 14,
    color: webColors.textPrimary,
    fontWeight: '500',
  },
  professionPillSubtitle: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  professionIntlRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  professionIntlIcon: {
    fontSize: 12,
    marginRight: 8,
    marginTop: 2,
  },
  professionIntlText: {
    fontSize: 12,
    color: webColors.textMuted,
    fontStyle: 'italic',
    flex: 1,
  },
  professionCountryRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  professionCountryIcon: {
    fontSize: 12,
    marginRight: 8,
  },
  professionCountryText: {
    fontSize: 12,
    color: webColors.textMuted,
  },

  // Resume Display
  resumeDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    marginBottom: 16,
  },
  resumeIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#dcfce7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resumeInfo: {
    flex: 1,
  },
  resumeTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  resumeStatus: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  resumeArrow: {
    fontSize: 16,
    color: webColors.textMuted,
  },

  // Education List
  educationList: {
    gap: 12,
    marginBottom: 16,
  },
  educationItem: {
    flexDirection: 'row',
    gap: 16,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  educationIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#fef3c7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  educationInfo: {
    flex: 1,
  },
  educationDegree: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  educationSchool: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 2,
  },
  educationYear: {
    fontSize: 13,
    color: webColors.textMuted,
  },

  // Privacy Policy
  privacyBox: {
    padding: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    marginBottom: 16,
    maxHeight: 200,
  },
  privacyText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
    marginBottom: 12,
  },
  privacyBold: {
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.primary,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },

  // Help & Support
  helpOptions: {
    gap: 12,
  },
  helpOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  helpOptionIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpOptionInfo: {
    flex: 1,
  },
  helpOptionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  helpOptionDesc: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  helpOptionArrow: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  backButton: {
    marginBottom: 20,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },

  // FAQ
  faqList: {
    gap: 12,
  },
  faqItem: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
    overflow: 'hidden',
  },
  faqItemOpen: {
    backgroundColor: webColors.primaryLight,
  },
  faqQuestion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingHorizontal: 20,
  },
  faqQuestionText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  faqQuestionTextOpen: {
    color: webColors.primaryDark,
  },
  faqArrow: {
    marginLeft: 12,
  },
  faqArrowOpen: {
    transform: [{ rotate: '180deg' }],
  },
  faqAnswer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },

  // Feedback Form
  feedbackForm: {
    marginTop: 12,
  },
  feedbackTextarea: {
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 14,
    color: webColors.textPrimary,
    minHeight: 150,
    textAlignVertical: 'top',
    marginBottom: 16,
  },

  // Buttons
  btnSecondary: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  btnSecondaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.primary,
  },
  btnPrimary: {
    backgroundColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    ...webShadows.button,
    alignSelf: 'flex-start',
  },
  btnPrimaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.6,
  },

  // Delete Section
  deleteSection: {
    borderTopWidth: 1,
    borderTopColor: webColors.border,
    paddingTop: 32,
  },
  btnDelete: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: webColors.error,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  btnDeleteText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.error,
  },

  // Logout Section
  logoutSection: {
    marginTop: 24,
  },
  btnLogout: {
    backgroundColor: webColors.textSecondary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  btnLogoutText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Profession Edit Modal
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    width: '100%',
    maxWidth: 600,
    maxHeight: '80%',
    ...webShadows.large,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  modalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: webColors.backgroundAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalProfessionType: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: webColors.backgroundAlt,
    flexWrap: 'wrap',
  },
  modalProfessionTypeText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  modalProfessionTypeHint: {
    fontSize: 12,
    color: webColors.textMuted,
    fontStyle: 'italic',
  },
  modalSearchWrapper: {
    position: 'relative',
    marginHorizontal: 24,
    marginTop: 16,
    marginBottom: 12,
  },
  modalSearchIcon: {
    position: 'absolute',
    left: 14,
    top: 14,
    zIndex: 1,
  },
  modalSearchInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 8,
    paddingVertical: 12,
    paddingLeft: 42,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
  },
  modalScrollView: {
    maxHeight: 400,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  modalLoadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  modalLoadingText: {
    marginTop: 12,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  modalProfessionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: 10,
    marginBottom: 10,
  },
  modalProfessionItemSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  modalCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: webColors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: webColors.cardBackground,
  },
  modalCheckboxSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primary,
  },
  modalProfessionInfo: {
    flex: 1,
  },
  modalProfessionTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  modalProfessionTitleSelected: {
    color: webColors.primaryDark,
    fontWeight: '600',
  },
  modalProfessionSubtitle: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 2,
  },
  modalEmptyState: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  modalEmptyStateText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalCancelBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  modalSaveBtn: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: webColors.primary,
    borderRadius: 8,
    alignItems: 'center',
    ...webShadows.button,
  },
  modalSaveBtnDisabled: {
    opacity: 0.5,
  },
  modalSaveBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Incomplete Profile Popup
  incompleteOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    paddingHorizontal: 20,
  },
  incompletePopup: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    padding: 32,
    alignItems: 'center',
    ...webShadows.large,
  },
  incompletePopupIconRow: {
    marginBottom: 20,
  },
  incompletePopupIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#fef3c7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  incompletePopupTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  incompletePopupCaption: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  incompletePopupBtn: {
    width: '100%',
    backgroundColor: webColors.primary,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    ...webShadows.button,
    marginBottom: 12,
  },
  incompletePopupBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  incompletePopupDismiss: {
    paddingVertical: 8,
  },
  incompletePopupDismissText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textMuted,
  },

  // ==================== MOBILE VARIANTS ====================
  accountLayoutMobile: {
    flexDirection: 'column',
  },
  sidebarMobile: {
    width: '100%',
    position: 'relative',
    top: 0,
  },
  accountContentMobile: {
    flex: 0,
    width: '100%',
  },
  formGroupMobile: {
    width: '100%',
  },
  pageTitleMobile: {
    fontSize: 22,
  },
});

export default ProfileDoctorsScreen;
